import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { jwtDecode } from "jwt-decode";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  formatPercentage,
  RIDE_STATUSES,
  suggestDressagePlacings,
  TIE_BREAK_REASONS,
  TIE_BREAK_DISPLAY_FIELDS,
  classifyTestForPlacing,
  classifyDshbClassKind,
  DSHB_CLASS_KINDS,
  ruleRefForTieBreakReason,
  tieBreakDisplayFieldForClassType,
  DRESSAGE_PLACING_CLASS_TYPES,
  PLACING_VIEWS,
  resolveDisplayTermReason,
  suppressScoreValue,
  filterVisibleClasses,
} from "score-common";
import { useTranslation } from "i18n-fe";
import { TableSkeleton, useActiveShow, RideStatusBadge, useActiveOrg } from "common-fe";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { useRideStatuses } from "../hooks/useRideStatuses";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { useCatalogDefs } from "../hooks/useCatalogDefs";
import RideLink from "../components/RideLink";
import ClassLink from "../components/ClassLink";
import FvdPushStatusIcon from "../components/FvdPushStatusIcon";
import { authoritativeSheetsOnly } from "../util/contenderCompare";
import {
  classNumberLabel,
  classDayKey,
  compareByClassNumber,
  formatClassStartTime,
  todayDayKey,
  getRideTestName,
  getCatalogTestById,
} from "../util/classDisplay";
import { classNeedsPlacings } from "../util/classNeedsPlacings";
import { getStoredPlacingView, storePlacingView } from "../util/placingViewPref";
import { hasOverridePlacedClassPermission } from "../permissions";

// #559 — how often the live status badges re-poll the batch endpoint. 10s is
// frequent enough to feel realtime during a running class without hammering the
// API (the hook also pauses while the tab is hidden).
const RIDE_STATUS_POLL_MS = 10000;

// #839 — batch-entry placing editor + single "Place" button (replaces the old
// per-row auto-save-on-blur flow — bugs #50/#60/#66: random order, no save,
// no confirmation). Shows the multi-judge result for every ride in a class
// (getClassResults, already sorted placing-asc then composite-desc — see
// score-api handlers/results.js). The operator enters ALL placings for the
// class into local edit buffers; NOTHING is written to score-api until Place
// is pressed, which commits every row in one atomic request
// (ResultsApi.setClassPlacings, a DynamoDB TransactWriteCommand server-side)
// and — only when the whole class is releasable (every non-scratched ride
// CONFIRMED/FINAL, mirroring foxbox's isReleased) — makes those placings
// available to foxbox's next FVD pull. If the class isn't fully releasable
// yet, the server blocks the ENTIRE commit (409, nothing written) and names
// the rides still holding it up.
//
// getClassResults doesn't carry a rider/horse label (only rideId/classId/
// perJudge/composite/collectiveTotal/placing/status/completedCount/
// expectedJudgeCount — collectiveTotal added by #946, same withhold-until-
// every-judge-confirmed policy as composite), so rows are labeled by joining
// against rides.listRides(classId) — the same join RideEditor's RidePicker
// does for its ride select.

// #822 hook — scoresheet-gen + competitor-email automation attaches HERE.
// This card (#839) guarantees this is called exactly once, synchronously
// after a SUCCESSFUL (releasable) Place commit, with the classId and the
// server's { classId, placed, releasable } response — and that a failure in
// here never blocks or hides the success confirmation already shown to the
// operator. #822 replaces this no-op with the real fire-and-forget call.
async function triggerScoresheetGenAndEmail(/* classId, placeResponse */) {
  // no-op — implemented by #822.
}


// #811 — a ride that can never earn a competitive placing (scratched before
// it ran, or eliminated/withdrawn/retired mid-test) doesn't belong mixed in
// with placeable rides, regardless of the rider's division. SCRATCHED is the
// raw stored Ride status (ridesById mirrors listRides, so this reads the same
// field ClassManager/RideEditor already gate on); "eliminated" has no status
// of its own — it's any confirmed sheet carrying a non-empty eliminations
// list, exactly how score-api's buildRideResult flags entry.eliminated on
// perJudge (mirrors calculation.js's `eliminated` — WD/retired rides are
// logged as an elimination reason, so this one check also covers "terminated").
//
// #945 — this is deliberately the ONLY "term'd" check (AC3: scratch/no-show/
// eliminated/withdrawn all sort to the bottom). There is no broader status
// enum to reach for: FVD has no RET/WD/no-show taxonomy of its own — foxbox's
// mapRideStatus (apps/foxbox/src/read/mapper.js) collapses EVERY non-empty
// chrTermReason (scratch, no-show, withdraw, retire, ...) into the single
// stored status "SCRATCHED" before it ever reaches score-api, keeping the raw
// reason only as ride.termReason (display-only). score-common's
// DERIVED_RIDE_STATUSES (utils/ride-status.js) mirrors the same SCRATCHED
// value and adds no separate WITHDRAWN/NO_SHOW member. A mid-test withdrawal
// is likewise not a status — RideEditor's ELIMINATION_REASONS includes
// "Withdrawn / retired" as an elimination reason, landing in perJudge[].
// eliminated exactly like any other elimination. So SCRATCHED + eliminated
// together are already the complete terminal set.
function isBottomTierRow(row, ridesById) {
  if (ridesById[row.rideId]?.status === RIDE_STATUSES.SCRATCHED) return true;
  return !!row.perJudge?.some((j) => j.eliminated);
}

// #529 — the explicit ordering used on every load/refetch (initial select AND
// post-Place refresh): placing ascending (unplaced rides last), then ride
// number ascending as a stable, predictable tie-break. #811 — scratched and
// eliminated/terminated rides are pulled into a forced-last tier ahead of
// that comparison, so they always sink to the bottom regardless of placing.
function sortResults(results, ridesById) {
  return [...results].sort((a, b) => {
    const aBottom = isBottomTierRow(a, ridesById);
    const bBottom = isBottomTierRow(b, ridesById);
    if (aBottom !== bBottom) return aBottom ? 1 : -1;

    const ap = a.placing;
    const bp = b.placing;
    if (ap != null && bp != null) {
      if (ap !== bp) return ap - bp;
    } else if (ap != null) {
      return -1;
    } else if (bp != null) {
      return 1;
    }
    const an = ridesById[a.rideId]?.rideNumber ?? Infinity;
    const bn = ridesById[b.rideId]?.rideNumber ?? Infinity;
    return an - bn;
  });
}

// #945 — the placing VIEWS layered on top of the batch-entry table.
// "default" is the pre-#945 row order (sortResults — placing asc then ride
// number, bottom-tier last) rendered exactly as before, no grouping/
// separators, so existing behavior (and every test written against it) is
// byte-for-byte unchanged when the operator hasn't touched the toggle. The
// two ADDED views: "score" ranks every ride by composite % highest-first,
// ignoring division; "division" groups by the rider's division
// (Ride.riderStatus, #821) with a separator per group, each group internally
// ranked by composite %.
// #953 — "suggested" adds a USEF rule-based placing suggestion (advisory
// only, see suggestedPlacingsFor below) as a fourth view alongside #945's
// three.
//
// #2000 — PLACING_VIEWS itself moved to score-common (imported below) so
// score-api's setShowDefaultPlacingView validates a show-level default
// against the exact same four strings this page renders, rather than a
// second hand-kept copy that could drift out of sync.
//
// #2000 — resolution order for the view picker's INITIAL value on a show
// switch:
//   1. A class-level preference, where the operator has already picked one
//      for this page view — this function is never called again for the
//      same show, so a manual selection is simply left standing (see the
//      appliedDefaultViewShowIdRef effect below, which calls this exactly
//      once per activeShowId).
//   2. This show's `defaultPlacingView` (Show.defaultPlacingView,
//      score-api's setShowDefaultPlacingView), when it's a value
//      PLACING_VIEWS still recognizes.
//   3. The operator's persisted last manual pick for this page
//      (util/placingViewPref.js's localStorage slot, #1595): sticky across
//      visits and show switches, per device. A show-level default still
//      outranks it — that one is an explicit configured decision about a
//      particular show (#2000), while this only fills the "nothing
//      configured" slot the system default used to occupy alone.
//   4. The system default. Card #2001 (bugs board #168): with no pick, no
//      show-level default and nothing persisted, the system default is
//      PLACING_VIEWS.DIVISION (BY RIDER STATUS/DIV), not PLACING_VIEWS.DEFAULT
//      (plain placing order) — riders reviewing a class expect it grouped by
//      division out of the box. PLACING_VIEWS.DEFAULT remains a selectable
//      view in the picker, it is simply no longer what an unconfigured show
//      opens on.
// A garbage/stale stored value at either stored slot (e.g. an old show row
// or an old localStorage entry from before a view was retired) falls
// through to the next step rather than passing an unrecognized value to
// buildPlacingGroups.
export function resolvePlacingView(show) {
  const stored = show?.defaultPlacingView;
  if (Object.values(PLACING_VIEWS).includes(stored)) return stored;
  return getStoredPlacingView() || PLACING_VIEWS.DIVISION;
}

const TERMD_GROUP_KEY = "__termd__";
const NO_DIVISION_KEY = "__no_division__";

// #945/#946 — the single composite-percentage comparator every placing view
// sorts with (highest first; a row with no composite yet sorts last). Kept
// as a standalone, reusable comparator — not baked into a bigger sort — so
// #946's tie-breaking card can wrap or replace just this function without
// touching the grouping/view logic below.
//
// #946 — a composite tie is broken by row.collectiveTotal (sum of every
// entered collective-mark score across the ride's sheets, computed
// server-side in score-api's buildRideResult — see the getClassResults
// comment above): higher total wins the tie (AC1). Equal totals fall
// through to 0, which — combined with Array.prototype.sort being spec-
// guaranteed stable — leaves the two rows in whatever order the API
// returned them, i.e. a true tie that doesn't get arbitrarily reordered
// (AC2). Missing collectiveTotal (a PARTIAL ride, or old data predating
// #946) sorts after any known value, same nulls-last policy as composite.
export function compareByComposite(a, b) {
  const ac = a?.composite;
  const bc = b?.composite;
  if (ac == null && bc == null) return 0;
  if (ac == null) return 1;
  if (bc == null) return -1;
  if (bc !== ac) return bc - ac;

  const at = a?.collectiveTotal;
  const bt = b?.collectiveTotal;
  if (at == null && bt == null) return 0;
  if (at == null) return 1;
  if (bt == null) return -1;
  return bt - at;
}

function sortByComposite(rows) {
  return [...rows].sort(compareByComposite);
}

// #946 — +/-/= indicator (AC3) for a group of rows that share the same
// rendered "competitive pool" (one buildPlacingGroups group — the same unit
// sortByComposite already sorts within). Groups rows by composite value
// (not adjacency — the "default" view's rows aren't composite-sorted, so a
// tie needs to be found by value everywhere the indicator is shown), then
// within each 2+ group ranks the KNOWN collectiveTotals:
//   - a row whose collectiveTotal is missing (null/undefined — a PARTIAL
//     ride, or a sheet confirmed before #946 shipped) is UNKNOWN, not a
//     loss: it gets no indicator at all, never "-" (never returned in the
//     map).
//   - among rows with a known collectiveTotal, the top value wins: if
//     exactly ONE row holds it, that row gets "+" (won the tie-break
//     outright). If TWO OR MORE rows share the top value, they're tied WITH
//     EACH OTHER, not each a sole winner — they all get "=" instead (this is
//     also what makes an across-the-board equal group, AC2's true tie, come
//     out "=" for everyone: the whole known set IS the top tier). Any known
//     value below the top gets "-".
//   - fewer than 2 rows with a KNOWN collectiveTotal in the group means
//     there's nothing to compare (can't tell if the lone known value beat an
//     unknown one) — no indicators at all for that group.
// Returns a rideId -> "+"|"-"|"=" Map.
function tieIndicatorsFor(rows) {
  const byComposite = new Map();
  for (const row of rows) {
    if (row?.composite == null) continue;
    const group = byComposite.get(row.composite);
    if (group) group.push(row);
    else byComposite.set(row.composite, [row]);
  }

  const indicators = new Map();
  for (const group of byComposite.values()) {
    if (group.length < 2) continue;
    const known = group.filter((r) => r.collectiveTotal != null);
    if (known.length < 2) continue;

    const max = Math.max(...known.map((r) => r.collectiveTotal));
    const atMax = known.filter((r) => r.collectiveTotal === max).length;
    for (const row of known) {
      if (row.collectiveTotal !== max) indicators.set(row.rideId, "-");
      else indicators.set(row.rideId, atMax > 1 ? "=" : "+");
    }
  }
  return indicators;
}

// #946 — the visible +/-/= glyph next to a tied row's composite. Every
// string comes from a static t() call (no dynamic key construction) so the
// FE-side i18n map stays literal-scannable. `value` is undefined for a
// non-tied row, in which case nothing renders. #953 — `labelOverride` lets
// the USEF-suggestion column reuse this same glyph/color plumbing for its
// own "=" (tied per the full rule chain) without adopting #946's composite-
// only aria-label text; `suppressTitle` drops the native `title` attribute
// (keeping aria-label for a11y) when the caller already wraps this glyph in
// its own MUI Tooltip, so hovering doesn't show two overlapping tooltips.
function TieIndicator({ value, rideId, t, labelOverride, suppressTitle }) {
  if (!value) return null;
  const meta =
    value === "+"
      ? { symbol: "+", label: labelOverride ?? t("placings.tieIndicator.higherLabel"), color: "success.main" }
      : value === "-"
        ? { symbol: "−", label: labelOverride ?? t("placings.tieIndicator.lowerLabel"), color: "error.main" }
        : { symbol: "=", label: labelOverride ?? t("placings.tieIndicator.equalLabel"), color: "text.secondary" };
  return (
    <Typography
      component="span"
      data-testid={`placing-tie-indicator-${rideId}`}
      aria-label={meta.label}
      title={suppressTitle ? undefined : meta.label}
      sx={{ ml: 0.75, fontWeight: 700, color: meta.color }}
    >
      {meta.symbol}
    </Typography>
  );
}

// #953 — static reason-code -> i18n-key map for the USEF suggestion tooltip
// (see suggestedPlacingsFor below). Keyed off score-common's TIE_BREAK_REASONS
// enum so every code the engine can return has a literal, static t() key here
// — no i18n key is ever built dynamically from the reason string itself.
const SUGGESTION_REASON_LABEL_KEYS = {
  [TIE_BREAK_REASONS.PERCENTAGE]: "placings.suggestion.reason.percentage",
  [TIE_BREAK_REASONS.COLLECTIVE_MARKS]: "placings.suggestion.reason.collectiveMarks",
  [TIE_BREAK_REASONS.YOUNG_HORSE_AVERAGE]: "placings.suggestion.reason.youngHorseAverage",
  [TIE_BREAK_REASONS.YOUNG_HORSE_SUBMISSION]: "placings.suggestion.reason.youngHorseSubmission",
  [TIE_BREAK_REASONS.FEI_CHILDREN_TECHNICAL]: "placings.suggestion.reason.feiChildrenTechnical",
  [TIE_BREAK_REASONS.FEI_CHILDREN_MEDIAN]: "placings.suggestion.reason.feiChildrenMedian",
  [TIE_BREAK_REASONS.FREESTYLE_ARTISTIC]: "placings.suggestion.reason.freestyleArtistic",
  [TIE_BREAK_REASONS.FREESTYLE_HARMONY]: "placings.suggestion.reason.freestyleHarmony",
  [TIE_BREAK_REASONS.FREESTYLE_CHOREOGRAPHY]: "placings.suggestion.reason.freestyleChoreography",
  // #1752 — Dressage Seat Equitation / Medal (USEF DR133): the judge wrote
  // the place on the sheet, so what "separated" two rows is the judge's pen.
  [TIE_BREAK_REASONS.JUDGE_ENTERED]: "placings.suggestion.reason.judgeEntered",
  // #2209 — DSHB Championship/Suitability (USEF DR210.5/DR204.2.c): kept as
  // its own key (not reusing judgeEntered's) so the tooltip never cites the
  // wrong rule family for a breed-show class — score-common already keeps
  // DSHB_JUDGE_ENTERED distinct from JUDGE_ENTERED for the same reason.
  [TIE_BREAK_REASONS.DSHB_JUDGE_ENTERED]: "placings.suggestion.reason.dshbJudgeEntered",
  // #2206 — DSHB's own tie-break step (DR211.4, docs/rules/usef/
  // rulebook-dr-dressage-2026.pdf, "© USEF 2026"), verbatim: "Tied scores
  // shall be broken first by referring to the totals of the movement
  // scores." score-common's placing.js already stamps
  // TIE_BREAK_REASONS.MOVEMENT_TOTALS with RULE_REFS "DR211.4"; this map
  // had no key for it, so the "separated by ... (DR211.4)" tooltip clause
  // silently dropped for the one class type it exists to explain.
  [TIE_BREAK_REASONS.MOVEMENT_TOTALS]: "placings.suggestion.reason.movementTotals",
};

// #953 — a shared placing used to always cite DR123.3's "the horses must
// remain tied" language, because suggestDressagePlacings only ever stamped
// tiedReason as TIE_BREAK_REASONS.UNRESOLVED.
//
// #1752 — no longer fixed. A Dressage Seat Medal tie stamps
// RIDE_OFF_REQUIRED and cites DR133.2.g.5, which says the opposite of
// "remain tied": the riders owe an additional independent test in the ring.
// Showing DR123.3 on that row would tell the operator the tie stands, which
// is the wrong instruction. Both the citation and the sentence are now
// looked up per row off the tiedReason.
const SUGGESTION_TIED_TEXT_KEYS = {
  [TIE_BREAK_REASONS.UNRESOLVED]: "placings.suggestion.tiedText",
  [TIE_BREAK_REASONS.RIDE_OFF_REQUIRED]: "placings.suggestion.rideOffText",
  // #2209 — DSHB Championship/Suitability's own terminal (DR211.4, not
  // DR123.3): kept distinct from tiedText's UNRESOLVED key for the same
  // wrong-rule-family reason score-common keeps DSHB_UNRESOLVED distinct
  // from UNRESOLVED.
  [TIE_BREAK_REASONS.DSHB_UNRESOLVED]: "placings.suggestion.dshbUnresolvedText",
};

// #1288 — resolve the class's DRESSAGE_PLACING_CLASS_TYPES the same way
// score-api's checkPlacingImpactOnCommit does (results.js): the first ride
// whose testId resolves to a real catalog Test speaks for the whole class
// (same homogeneity assumption, same STANDARD default when nothing
// resolves — see that function's own comment for the caveat on mixed
// classes). Unlike the server, which fetches each Test from DynamoDB, this
// resolves entirely client-side off the already-loaded `ridesById`: every
// Ride carries its own testId (#915), and getCatalogTestById is a
// synchronous lookup against catalogDefsById (#1665: the LIVE score-catalog
// table, hydrated into IndexedDB by useCatalogDefs — see classDisplay.js),
// so no extra network round trip is needed here.
//
// #2209 — checked FIRST, off the selected CLASS's own className via
// classifyDshbClassKind (score-common), the same signal score-api's
// effectiveTestForCalculation (scoresheet.js) uses server-side to pick the
// "placement-only" scoringModel override. Independent of the per-row test
// loop below: FVD stamps a championship/suitability class with its
// QUALIFYING class's Test id (classifyDshbClassKind's own doc comment), so
// the real catalog Test this screen resolves per ride never carries
// anything that would let classifyTestForPlacing recognize a DSHB class on
// its own — the class's real name is the only reliable signal available
// client-side too. DR211.5 (docs/rules/usef/rulebook-dr-dressage-2026.pdf,
// © USEF 2026): "all scores (if given) and placings must be published" —
// a place-only class's rides have to reach this suggestion the same as any
// other class type.
//
// #2209 (judge review, FIX 1) — mirrors score-api's resolveClassTypeForPlacing
// (results.js): the className match ALONE is not the gate.
// classifyDshbClassKind's CHAMPIONSHIP_NAME_PATTERN is a bare className
// regex with no DSHB gate of its own (#2202's job, not this card's), so it
// fires just as readily on an ordinary percentage-scored
// "USDF Regional Championship Fourth Level Open" class. Routing that class
// to DSHB_PLACEMENT_ONLY would blank its Place-screen suggestion (empty
// buildChain by design) and its tie-break column
// (tieBreakDisplayFieldForClassType), even though it has real percentages
// to rank on. `rows` here already carries each ride's computed `composite`
// (getClassResults' shape, the same field suggestedPlacingsFor filters on
// below) — checked across every row so an all-still-PARTIAL DSHB class
// (composite genuinely null for every row so far) is not mistaken for a
// class with no percentage at all having its rows filtered out first.
function resolveClassTypeForSuggestion(rows, ridesById, catalogDefsById, classItem) {
  // #2277 — override wins over the className heuristic.
  const dshbKind = classifyDshbClassKind({
    className: classItem?.className,
    dshbKindOverride: classItem?.dshbClassKindOverride,
  });
  const dshbNamedPlacementOnly =
    dshbKind === DSHB_CLASS_KINDS.CHAMPIONSHIP || dshbKind === DSHB_CLASS_KINDS.SUITABILITY;
  const rowsCarryNoPercentage = !(rows ?? []).some(
    (r) => typeof r?.composite === "number" && Number.isFinite(r.composite)
  );
  if (dshbNamedPlacementOnly && rowsCarryNoPercentage) {
    return DRESSAGE_PLACING_CLASS_TYPES.DSHB_PLACEMENT_ONLY;
  }
  for (const row of rows) {
    const test = getCatalogTestById(ridesById[row.rideId]?.testId, catalogDefsById);
    if (test) return classifyTestForPlacing(test);
  }
  return DRESSAGE_PLACING_CLASS_TYPES.STANDARD;
}

// #1999: i18n column-header key for the active tie-break display field
// (see tieBreakField above). Static per-field lookup, not built from the
// field string, so every case has a literal t() key.
const TIE_BREAK_COLUMN_LABEL_KEYS = {
  [TIE_BREAK_DISPLAY_FIELDS.COLLECTIVE_MARKS]: "placings.columns.collectiveMarks",
  [TIE_BREAK_DISPLAY_FIELDS.ARTISTIC_MARKS]: "placings.columns.artisticMarks",
  // #2206 — DR211.4's own display number (DSHB only, see
  // tieBreakDisplayFieldForClassType above); this map had no key for it,
  // so the column rendered with t(undefined) as its header: a blank-headed
  // column of numbers in front of a judge deciding placings.
  [TIE_BREAK_DISPLAY_FIELDS.MOVEMENT_TOTALS]: "placings.columns.movementTotal",
};

// #1999/#2077: renders row[tieBreakField] (a raw points sum, DR123.3/
// DR129.5's own basis, not a percentage, so formatPercentage does not
// apply). null means "not known yet" (a PARTIAL ride, or old data), shown
// as a dash rather than 0 (same "unknown, not zero" contract as
// collectiveTotal itself, see sumCollectiveTotal/sumArtisticTotal in
// score-api).
//
// #2077: ONE decimal, not three. This is a sum of individually-awarded
// collective/artistic marks, each a coefficient times a mark scored in
// DR122.8's default half-point increments (0.5) or one of DR122.9's
// enumerated 0.1 grants (Freestyle artistic, Young Horse, 7-year-old/
// Children quality-only) (`docs/rules/usef/rulebook-dr-dressage-2026.pdf`,
// "© USEF 2026", current edition per docs/rules/README.md). Neither DR122.8
// nor DR122.9 constrains the coefficient itself to an integer — one decimal
// is lossless here only because every catalog def's actual coefficients are
// 1/2 (collectives) or 2/4 (artistic movements), never a fraction that
// would need a second digit; a future catalog def with a fractional
// coefficient would need this re-checked, not assumed. DR123.3's Young
// Horse tie-break ("combined and divided by two") is a rule-sanctioned
// 0.05-granularity path, but this column never renders it: the candidates
// built above carry no youngHorse.* sub-marks (see the block comment
// higher in this file), so today's actual render inputs are never finer
// than a tenth — that is a fact about what reaches this function today, not
// a guarantee the rule itself makes. DR123.3 itself calls the ordinary
// (non-Young-Horse) case a "total marks" comparison, not a percentage —
// matching the app's own existing convention for a mark-based total:
// report-defs' `mark()` helper (packages/report-defs/src/helpers.js)
// already renders every dressage mark at one decimal, and score-common's
// `formatPoints` renders a standard (non-freestyle) raw-score points total
// at one decimal too. Only `formatPercentage` (the Composite column, AC2)
// stays at three decimals — that's a real percentage, unaffected by this
// change.
function formatTieBreakValue(value) {
  return typeof value === "number" ? value.toFixed(1) : "-";
}

// #953 / #1288 — maps placeable rows into USEF placing-suggestion candidates
// and returns a rideId -> suggestion Map via score-common's
// suggestDressagePlacings (packages/score-common/src/utils/placing.js; full
// rule citations in docs/score/usef-dressage-placing-rules.md), using the
// class's real classType (resolveClassTypeForSuggestion above) instead of
// always forcing STANDARD.
//
// The candidates built below still only ever carry { percentage,
// collectiveTotal, movementTotal } — no freestyle.*/feiChildren.*/
// youngHorse.* sub-marks
// (submission/perspective, technical, artistic/harmony/choreography), which
// getClassResults' per-row shape (perJudge/composite/collectiveTotal/
// placing/status/completedCount/expectedJudgeCount) doesn't carry today —
// so this does NOT implement DR129.5's real FREESTYLE_ARTISTIC/HARMONY/
// CHOREOGRAPHY or FEI_CHILDREN_TECHNICAL/MEDIAN tie-break steps. What
// resolving the real classType DOES fix: FREESTYLE and FEI_CHILDREN's own
// chains have no COLLECTIVE_MARKS step at all, so an exact percentage tie in
// one of those classes now correctly renders as tied (DR129.5: collective
// marks must not break it) instead of being silently split by
// collectiveTotal, a comparison those rules don't call for. YOUNG_HORSE
// keeps COLLECTIVE_MARKS as its own step 2 same as STANDARD, so it's
// unaffected either way with today's candidate shape. A row whose composite
// is still withheld (PARTIAL, #697) can't be ranked at all and is simply
// absent from the returned Map — advisory UI only, so "no suggestion yet"
// beats a fabricated one.
function suggestedPlacingsFor(rows, ridesById, catalogDefsById, classItem) {
  const classType = resolveClassTypeForSuggestion(rows, ridesById, catalogDefsById, classItem);
  const isDshbPlacementOnly = classType === DRESSAGE_PLACING_CLASS_TYPES.DSHB_PLACEMENT_ONLY;
  // #2209 — a DSHB_PLACEMENT_ONLY ride's composite is null by design
  // (score-common's calculation.js placement-only branch omits
  // percentage/totalPoints entirely, #2183): dropping it here on
  // `composite != null` is exactly the bug this card fixes, and left the
  // Place screen suggesting nothing at all for a place-only class. DR211.5
  // (docs/rules/usef/rulebook-dr-dressage-2026.pdf, © USEF 2026): "all
  // scores (if given) and placings must be published" — the "(if given)"
  // is exactly this case, so the composite gate must not apply to this
  // classType.
  //
  // #2209 (judge review) — bypassing `composite != null` also bypasses
  // #697's own withholding contract, whose doc comment above (the block
  // just above this function) says a PARTIAL row "is simply absent from
  // the returned Map". Without a status check a half-judged DSHB
  // Championship/Suitability class would put every not-yet-judged row
  // (judgeEnteredPlace: null) into one large tie group at the bottom
  // instead of withholding them the way every other classType already
  // does — presentational only (no score leaks either way), but an
  // undeclared change to a stated contract, so the same withhold-until-
  // PARTIAL-is-over rule applies here too.
  const candidates = rows
    .filter((r) => r.composite != null || (isDshbPlacementOnly && r.status !== "PARTIAL"))
    .map((r) => ({
      id: r.rideId,
      percentage: r.composite,
      collectiveTotal: r.collectiveTotal,
      // #2206 — DR211.4's own operand (buildChain's DSHB case,
      // score-common/utils/placing.js: { code: MOVEMENT_TOTALS, get: (c) =>
      // c.movementTotal }). Without this the DSHB chain's tier-2 step has
      // no data to compare even once getClassResults' row carries a real
      // movementTotal (#2260), and a percentage tie in a DSHB class would
      // silently fall through to UNRESOLVED ("must remain tied") instead of
      // ever separating on movement-score totals.
      movementTotal: r.movementTotal ?? null,
      // #1752 — the judge's own Place-column entry, carried on the
      // getClassResults row. For an EQUITATION/EQUITATION_MEDAL class
      // (USEF DR133) or a DSHB_PLACEMENT_ONLY class (USEF DR211.5, #2209)
      // this IS the suggestion: the judge writes the place directly and
      // this screen shows it back for the operator to commit, rather than
      // ranking percentages. Ignored for every other classType.
      judgeEnteredPlace: r.judgeEnteredPlace ?? null,
    }));
  return new Map(suggestDressagePlacings(candidates, { classType }).map((s) => [s.id, s]));
}

// #953 — builds the tooltip string for one row's USEF suggestion: cites
// what separated this row's placing GROUP from the next-ranked group below
// (suggestion.reason/ruleRef) and/or that the row remains tied with its
// group (suggestion.tiedReason), so the operator always sees the rule
// reference driving what's on screen. Returns null when neither applies
// (the single last-ranked, untied group has nothing below it to compare
// against).
function suggestionTooltip(suggestion, t) {
  const parts = [];
  // #1752 — static literal keys only (never built from the reason string),
  // same rule the SUGGESTION_REASON_LABEL_KEYS map above follows.
  const tiedTextKey = suggestion.tiedReason
    ? SUGGESTION_TIED_TEXT_KEYS[suggestion.tiedReason]
    : null;
  if (suggestion.tieGroup.length > 1 && tiedTextKey) {
    parts.push(
      t(tiedTextKey, {
        count: suggestion.tieGroup.length - 1,
        ruleRef: ruleRefForTieBreakReason(suggestion.tiedReason),
      })
    );
  }
  const reasonKey = suggestion.reason ? SUGGESTION_REASON_LABEL_KEYS[suggestion.reason] : null;
  if (reasonKey) {
    parts.push(
      t("placings.suggestion.separatedText", {
        reasonLabel: t(reasonKey),
        ruleRef: suggestion.ruleRef,
      })
    );
  }
  return parts.length > 0 ? parts.join(" ") : null;
}

// #945 — riderStatus is FVD free text (tblRider.chrStatus), not a closed
// enum, so "Open" / "open " / "OPEN" are the same division typed three
// different ways in FVD and must not split into three separator groups.
// Group by a trimmed+case-folded key; the DISPLAYED label keeps the
// first-seen original (trimmed) casing rather than inventing one. #2049 —
// extracted so the #904 duplicate-placing guard in the division view scopes
// by the SAME normalized key the displayed grouping uses, so the guard can
// never disagree with what the operator sees on screen.
function divisionKeyForRow(row, ridesById) {
  const raw = ridesById[row.rideId]?.riderStatus;
  const trimmed = typeof raw === "string" ? raw.trim() : "";
  return trimmed ? trimmed.toLowerCase() : NO_DIVISION_KEY;
}

// #945 — builds the ordered { key, label, rows }[] groups the table renders
// (one separator row per group with a non-null label). "default" is a single
// pass-through group over `results` AS-IS — results is already sortResults-
// ordered on load, so this reproduces the pre-#945 render exactly, no
// separator. Term'd rides (scratched/eliminated/WD/retired — #811's
// isBottomTierRow; there is no dedicated rider-outcome status enum, so this
// reuses the same bottom-tier check as the default sort) never earn a
// competitive placing regardless of view or division, so the "score" and
// "division" views always pull them into one forced-last group (AC3) ahead
// of the per-view grouping. "score" yields a single unlabeled group (so only
// the term'd separator, if any, renders) plus the term'd group; "division"
// yields one group per Ride.riderStatus (#821), sorted alphabetically, with
// rides that resolve no division grouped last among the named ones, then
// the term'd group.
function buildPlacingGroups(results, ridesById, mode, t, catalogDefsById, classItem) {
  if (mode === PLACING_VIEWS.DEFAULT) {
    return [{ key: "all", label: null, rows: results }];
  }

  const placeable = [];
  const termd = [];
  for (const row of results) {
    (isBottomTierRow(row, ridesById) ? termd : placeable).push(row);
  }

  const groups = [];
  if (mode === PLACING_VIEWS.DIVISION) {
    const byDivision = new Map(); // normalizedKey -> { label, rows }
    for (const row of placeable) {
      const key = divisionKeyForRow(row, ridesById);
      const raw = ridesById[row.rideId]?.riderStatus;
      const trimmed = typeof raw === "string" ? raw.trim() : "";
      const label = trimmed ? trimmed : t("placings.view.noDivision");
      const existing = byDivision.get(key);
      if (existing) {
        existing.rows.push(row);
      } else {
        byDivision.set(key, { label, rows: [row] });
      }
    }
    const namedKeys = [...byDivision.keys()]
      .filter((key) => key !== NO_DIVISION_KEY)
      .sort((a, b) => a.localeCompare(b));
    for (const key of namedKeys) {
      const { label, rows } = byDivision.get(key);
      groups.push({ key, label, rows: sortByComposite(rows) });
    }
    if (byDivision.has(NO_DIVISION_KEY)) {
      const { label, rows } = byDivision.get(NO_DIVISION_KEY);
      groups.push({ key: NO_DIVISION_KEY, label, rows: sortByComposite(rows) });
    }
  } else if (mode === PLACING_VIEWS.SUGGESTED) {
    // #953 — order placeable rows by the USEF suggestion's placing number.
    // A row with no suggestion yet (composite still withheld, #697) has no
    // rank to slot in by, so it's appended after the ranked ones in
    // composite-desc order (same fallback the "score" view uses) rather than
    // guessed into a position.
    const suggestions = suggestedPlacingsFor(placeable, ridesById, catalogDefsById, classItem);
    const ranked = [];
    const unranked = [];
    for (const row of placeable) {
      (suggestions.has(row.rideId) ? ranked : unranked).push(row);
    }
    ranked.sort((a, b) => suggestions.get(a.rideId).placing - suggestions.get(b.rideId).placing);
    groups.push({ key: "all", label: null, rows: [...ranked, ...sortByComposite(unranked)] });
  } else {
    groups.push({ key: "all", label: null, rows: sortByComposite(placeable) });
  }

  if (termd.length > 0) {
    groups.push({
      key: TERMD_GROUP_KEY,
      label: t("placings.view.termd"),
      rows: sortByComposite(termd),
    });
  }
  return groups;
}

// #599/#1079/#2852 — a class's placing status, derived from its
// getClassResults rows (the only per-class signal the API exposes;
// listClasses carries no aggregate). The "final but not yet placed"
// question is the shared classNeedsPlacings predicate (#2852) — the SAME
// formula the Classes-page badge, the header count chip and the nav badge
// derive from via useClassesNeedingPlacings, so the two screens can never
// disagree again. getClassResults rows carry the judged status in `status`
// (FINAL / SCRATCHED / …), the predicate's own contract; see its doc
// comment in src/util/classNeedsPlacings.js for the full rule (all
// non-scratched rides FINAL — the #2113 server placing bar — with NO
// placing committed yet; #2852's driver decision supersedes #1079's
// "some placed, one blank still badges" rule, since a class with a
// terminated (eliminated/no-show/excused) ride would otherwise badge
// forever; placing 0 is a legitimate placing, #534; scratched rides
// excluded throughout, #920/#2840).
// #2855 — `anyPlaced` powers the default "hide already-placed classes"
// filter (the client's second ask): placing is a whole-class action (#2852's
// driver decision), so ANY committed placing on ANY row — 0 included (#534,
// a stored 0 is a real placing, not an absence) — means the operator has
// already been through this class and it drops out of the default list.
// Deliberately broader than finalUnplaced's negation: an in-progress or
// partially-placed class is still "already placed" for hiding purposes,
// while a class with a legacy placing on a terminated ride (#2057) hides
// too — the operator can always toggle the filter off to reach it.
function deriveClassSummary(rows) {
  return {
    hasRides: rows.length > 0,
    finalUnplaced: classNeedsPlacings(rows),
    anyPlaced: rows.some((r) => r.placing != null),
  };
}

// #803 — list order: scheduled time ascending (earliest → latest), with the
// class-number comparator as a stable tie-break (equal/missing times).
// Classes missing placings no longer float to the top (#599's behavior) —
// they're called out with a badge instead (below), so the operator can work
// the show in ring order and still spot what's outstanding at a glance.
// scheduledTime is FVD's Z-stamped naive-local string, but Date.parse orders
// consistently across the show, so relative ordering is correct even though
// the absolute value is UTC.
function sortClassesForPlacings(classes) {
  return [...(classes ?? [])].sort((a, b) => {
    const at = a.scheduledTime ? Date.parse(a.scheduledTime) : NaN;
    const bt = b.scheduledTime ? Date.parse(b.scheduledTime) : NaN;
    const aOk = !Number.isNaN(at);
    const bOk = !Number.isNaN(bt);
    if (aOk && bOk && at !== bt) return at - bt; // earliest first
    if (aOk !== bOk) return aOk ? -1 : 1; // scheduled classes before unscheduled
    return compareByClassNumber(a, b);
  });
}

// #839 — batch mode: a BLANK box means "leave unplaced" (null). This differs
// from the old per-row auto-save flow (#534), where blank had to mean 0
// because blur-to-save couldn't otherwise distinguish "no edit" from "clear
// it" — a batch form doesn't have that ambiguity, so blank reads as its plain
// meaning. A typed "0" is still a legitimate placing (#534), never confused
// with blank/unplaced.
function parsePlacingInput(raw) {
  const trimmed = (raw ?? "").trim();
  if (trimmed === "") return { ok: true, value: null };
  const n = Number(trimmed);
  if (!Number.isInteger(n) || n < 0) return { ok: false, value: null };
  return { ok: true, value: n };
}

export default function ClassPlacings() {
  const { t } = useTranslation("scoreadmin");
  useDocumentTitle(t("title.placings"));
  const scoreApi = useScoreApi();
  // scoreApi's identity is NOT stable across a background OIDC token refresh
  // (common-fe's useApiConfig rebuilds it whenever auth.user's access_token
  // changes, e.g. a silent renew) — a real operator can be mid-batch-entry
  // for many minutes. The effects below depend on stable primitives
  // (activeShowId/classId) and read scoreApi through this ref, so that churn
  // never reloads-and-wipes an in-progress Place entry out from under the
  // operator — exactly the class of silent data loss #839 exists to kill.
  const scoreApiRef = useRef(scoreApi);
  scoreApiRef.current = scoreApi;
  const { activeShowId, activeShow, setActiveShowId, loading: loadingShow } = useActiveShow();

  // #2862 — placed-class lock. Once this class's placings are SET (any row
  // carries a committed placing — the same loose `placing != null` signal
  // deriveClassSummary's anyPlaced and score-api's classHasCommittedPlacings
  // both use), the inputs + Place button go read-only for EVERYONE until an
  // override holder (score:override_placed_class — score_admin) presses the
  // explicit "Unlock to edit" action. The server enforces the same lock
  // (403 placed_class_locked on both placing write routes), so this is UX,
  // not authorization. Token-roles read mirrors ClassDetailDrawer's #2415
  // pattern (see src/permissions.js for why not common-fe's usePermission).
  const auth = useAuth();
  const { activeOrgId } = useActiveOrg();
  const canOverridePlacedClass = useMemo(() => {
    const accessToken = auth.user?.access_token;
    if (!accessToken || !activeOrgId) return false;
    try {
      const claims = jwtDecode(accessToken);
      const raw = claims.roles;
      const map = typeof raw === "string" ? JSON.parse(raw) : raw || {};
      return hasOverridePlacedClassPermission(map[activeOrgId]);
    } catch {
      return false;
    }
  }, [auth.user?.access_token, activeOrgId]);
  const [overrideUnlocked, setOverrideUnlocked] = useState(false);

  // #1069 — an admin can be deep-linked here with ?classId=<id> (from a
  // scoresheet commit that unplaced a class, RideEditor's placing-impact
  // banner). Read through a ref, same churn-avoidance pattern as
  // scoreApiRef above: useSearchParams returns a new object on every
  // render, and this is only ever read once per class-list load below, not
  // something the effect should re-run for.
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  // #1290 — the same #1069 banner link also emits a &showId= (the ride's
  // own show), for the case where the operator's active show isn't the
  // unplaced class's show. Previously nothing here read it, so the deep
  // link silently no-oped into an empty class picker (no classId match,
  // no explanation) whenever the two shows differed. Switch the active
  // show once, on mount, to whatever the link asked for; if that id turns
  // out invalid (wrong org, bad id, ...) ActiveShowProvider's own repair
  // effect falls back to shows[0], so this can't strand the picker.
  const appliedShowIdParamRef = useRef(false);
  useEffect(() => {
    if (appliedShowIdParamRef.current) return;
    appliedShowIdParamRef.current = true;
    const requestedShowId = searchParamsRef.current.get("showId");
    if (requestedShowId && requestedShowId !== activeShowId) {
      setActiveShowId(requestedShowId);
    }
  }, [activeShowId, setActiveShowId]);

  const [classes, setClasses] = useState([]);
  const [classSummaries, setClassSummaries] = useState({});
  const [classId, setClassId] = useState("");

  // #1994 — a class pick here was local React state only, never reflected
  // into the URL. Clicking through to a ride (RideLink -> /rides/:rideId)
  // and then hitting Back (RideEditor's `navigate(-1)`, #845) popped this
  // component back to its bare `/placings` URL, which remounts with no
  // `?classId=` to reselect (the effect below only reads it once per
  // classes load) — so the operator landed back on the unselected class
  // list ("main class placing screen") instead of the class they'd drilled
  // into. Mirroring the deep-link's own query param on every manual
  // selection keeps the URL in sync with what's on screen, so the history
  // entry `navigate(-1)` pops back to already carries the right class. This
  // PUSHes a real history entry per selection (default react-router-dom
  // behavior, same as ClassManager's per-class `/classes/:classId` Link) so
  // browser Back walks the picker's own history one class-pick at a time,
  // exactly as it already does for ClassManager — a `replace` here would
  // instead collapse the "nothing selected" root entry into whichever class
  // was picked first, leaving no history entry to pop back to underneath it.
  const selectClassId = useCallback(
    (nextClassId) => {
      setClassId(nextClassId);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (nextClassId) {
          next.set("classId", nextClassId);
        } else {
          next.delete("classId");
        }
        return next;
      });
    },
    [setSearchParams],
  );

  const [loadingResults, setLoadingResults] = useState(false);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [error, setError] = useState(null);

  const [results, setResults] = useState([]);
  const [ridesById, setRidesById] = useState({});
  // Card #2063 AC3 — the SPECIFIC DR123.3 term reason per terminated ride
  // (Scratched/Excused/Eliminated/No Show), keyed by rideId. Computed
  // client-side via score-common's deriveRideTermReason (the single 4-tier
  // read path — see docs/score/term-reason-vocabulary.md), never
  // re-derived by hand here.
  const [termReasonsById, setTermReasonsById] = useState({});

  // #1665 — the LIVE score-catalog table (GET /catalog, card #1662's
  // useCatalogDefs / IndexedDB cache), replacing the retired build-time
  // bundle as the source getRideTestName/getCatalogTestById (classDisplay.js)
  // resolve a ride's test against.
  const { catalogDefs } = useCatalogDefs();
  const catalogDefsById = useMemo(() => {
    const map = new Map();
    for (const def of catalogDefs) map.set(def.id, def);
    return map;
  }, [catalogDefs]);

  // Server-derived badge status per placing row (#544/#546), polled live (#559)
  // so a ride flipping to FINAL/scored during the show updates without a manual
  // reload. The hook pauses polling while the tab is hidden.
  const rideIds = useMemo(() => results.map((r) => r.rideId).filter(Boolean), [results]);
  const { statuses: rideStatuses } = useRideStatuses(rideIds, {
    intervalMs: RIDE_STATUS_POLL_MS,
  });

  // Per-row BATCH edit buffer: rideId -> the input's current string value.
  // Nothing here is sent to score-api until Place is pressed (#839) — reset
  // whenever results load/reload so the buffer always starts from the
  // server's current placings.
  const [editValues, setEditValues] = useState({});

  // Place-button submit state + the single banner that reports the outcome:
  // success (with a count), blocked (409 — class not releasable, names the
  // rides still holding it up), or a generic commit error.
  const [placeSubmitting, setPlaceSubmitting] = useState(false);
  const [placeBanner, setPlaceBanner] = useState(null);
  // #902 — inline resolve (force-final/scratch) on a single blocking ride
  // from the banner's list, without leaving the Placing screen. Tracks which
  // ride's action is in flight (disables just that row) plus a resolve-only
  // error, kept separate from placeBanner so a resolve failure doesn't stomp
  // the still-valid blocking list underneath it.
  const [resolvingRideId, setResolvingRideId] = useState(null);
  const [resolveError, setResolveError] = useState(null);

  // Classes for the active show. The list renders as soon as listClasses
  // returns (#599 — no dropdown/extra action to see the classes); each class's
  // placing summary then loads in the background and re-sorts final-but-unplaced
  // classes to the top without blocking the initial render.
  useEffect(() => {
    setClassId("");
    setClasses([]);
    setClassSummaries({});
    setResults([]);
    setRidesById({});
    if (!activeShowId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setLoadingClasses(true);
        setError(null);
        const list = unwrap(await scoreApiRef.current.classes.listClasses(activeShowId));
        if (cancelled) return;
        // #1117 -- hidden classes are not offered in the placings list.
        setClasses(filterVisibleClasses(list));
        setLoadingClasses(false);
        // #1069 — deep-link: only pre-select if the requested class
        // actually belongs to this (active) show's list. An unmatched or
        // absent classId leaves the picker exactly as it was (empty,
        // admin picks manually) — additive, not a behavior change.
        const requestedClassId = searchParamsRef.current.get("classId");
        if (requestedClassId && list.some((c) => c.id === requestedClassId)) {
          setClassId(requestedClassId);
        }
        // Per-class placing status: listClasses has no aggregate, so fetch each
        // class's results in parallel. A failed fetch degrades to "no summary"
        // (the class just doesn't get the top-of-list treatment or the chip).
        const summaries = {};
        await Promise.all(
          list.map(async (c) => {
            try {
              const rows = unwrap(await scoreApiRef.current.results.getClassResults(c.id));
              summaries[c.id] = deriveClassSummary(rows);
            } catch {
              summaries[c.id] = deriveClassSummary([]);
            }
          })
        );
        if (!cancelled) setClassSummaries(summaries);
      } catch (err) {
        if (!cancelled) {
          setError(err);
          setLoadingClasses(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeShowId]);

  // #1994 — the effect above (and #1069's requestedClassId read inside it)
  // only reruns on an actual REMOUNT (activeShowId changing, or navigating
  // in from a different route entirely, e.g. RideEditor's Back). But
  // selectClassId pushes a real history entry per class pick, so browser
  // Back/Forward between two `/placings` entries that differ ONLY in
  // `?classId=` is a same-route popstate:
  // react-router updates `searchParams` without unmounting this component,
  // so without this effect `classId` state would go stale on exactly that
  // hop (verified failing before this effect was added: the SECOND of two
  // consecutive Backs left the previous class's heading on screen after the
  // URL had already reverted to no `classId`). Mirrors the deep-link
  // validation above: an unmatched/absent id is treated as "no selection",
  // never a dead invalid classId.
  useEffect(() => {
    const paramClassId = searchParams.get("classId") || "";
    if (paramClassId === classId) return;
    if (paramClassId && !classes.some((c) => c.id === paramClassId)) return;
    setClassId(paramClassId);
    // classId deliberately excluded: this effect reacts to the URL, not to
    // its own writes, and including it would fight selectClassId's own
    // setClassId call on every manual pick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, classes]);

  const sortedClasses = useMemo(() => sortClassesForPlacings(classes), [classes]);

  // #803 — filterable/searchable class list, same match logic as ClassManager's
  // search (classNumberLabel + className, case-insensitive substring); also
  // matches ring since the list shows it as secondary text. The "Needs
  // placings only" toggle (product decision, follow-up to #803) composes with
  // search: default (off) keeps the full time-ordered list, on restricts to
  // classes that carry the "Needs placings" badge.
  //
  // #2855 — the client's first two asks are DEFAULT-ON filters composing
  // with the same stack: "today's classes only" (a class whose classDayKey
  // equals todayDayKey(), both the FVD naive-venue-wall-clock date domain —
  // see classDisplay.js) and "hide already-placed classes" (summary.anyPlaced,
  // see deriveClassSummary). Both initial values read the URL once on mount
  // (?today=0 / ?hidePlaced=0 opts out), same one-shot deep-link pattern as
  // #1069's ?classId= above — that keeps the pre-#2855 unfiltered list
  // reachable by link (and by the Cypress suites pinned to fixed-date
  // fixtures) without a race against the async per-class summary load.
  const [search, setSearch] = useState("");
  const [needsPlacingsOnly, setNeedsPlacingsOnly] = useState(false);
  const [todayOnly, setTodayOnly] = useState(() => searchParamsRef.current.get("today") !== "0");
  const [hidePlaced, setHidePlaced] = useState(
    () => searchParamsRef.current.get("hidePlaced") !== "0",
  );
  // Computed once per mount: the filter's "today" must not flip mid-session
  // while the operator works (a class list silently changing at midnight
  // would be worse than one that needs a reload).
  const todayKey = useMemo(() => todayDayKey(), []);
  const filteredClasses = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sortedClasses.filter((c) => {
      if (todayOnly && classDayKey(c) !== todayKey) return false;
      if (hidePlaced && classSummaries[c.id]?.anyPlaced) return false;
      if (needsPlacingsOnly && !classSummaries[c.id]?.finalUnplaced) return false;
      if (!q) return true;
      return `${classNumberLabel(c)} ${c.className ?? ""} ${c.ring ?? ""}`.toLowerCase().includes(q);
    });
  }, [sortedClasses, search, needsPlacingsOnly, todayOnly, hidePlaced, todayKey, classSummaries]);

  // #981 — the currently-selected class object, used by the placing screen's
  // top-right heading (see below): that heading is now the only linked
  // rendering of a class on this page (the picker's own entries are plain
  // text — see the ListItemText a few hundred lines down).
  const selectedClass = useMemo(
    () => classes.find((c) => c.id === classId) ?? null,
    [classes, classId],
  );

  // Results + rides (for labels) for the selected class. Re-run after a
  // successful Place commit (see handlePlace) so the table reflects the
  // freshly committed placings and re-sorts using the server's ordering.
  const loadResults = useCallback(async (id) => {
    // Reads scoreApi through the ref (see its doc comment above) so this
    // callback's own identity stays stable across an OIDC token refresh.
    const [resultList, rideList] = await Promise.all([
      scoreApiRef.current.results.getClassResults(id).then(unwrap),
      scoreApiRef.current.rides.listRides(id).then(unwrap),
    ]);
    // Card #2063 AC3 — one batched ScoreSheets call for the whole class's
    // rides (card #1961's listScoreSheetsForRides, the same batch endpoint
    // scribe-fe's useRidesForClass already uses), so deriveRideTermReason's
    // tier 4 (a lone sheet-level elimination not yet stamped onto
    // ride.terminationReason — the un-migrated #2056 legacy rows) resolves
    // correctly here too, not just tiers 1-3. Best-effort: a failure here
    // must not block the placings table itself from loading, so a fetch
    // error degrades to no sheets (tiers 1-3 still resolve every ride that
    // isn't relying on tier 4).
    const rideIds = rideList.map((r) => r.id);
    const sheetList = rideIds.length
      ? await scoreApiRef.current.scoreSheets
          .listScoreSheetsForRides({ rideIds })
          .then(unwrap)
          .catch(() => [])
      : [];
    return { resultList, rideList, sheetList };
  }, []);

  // Shared by the load effect below and handlePlace's post-commit refresh —
  // one place that turns a (resultList, rideList, sheetList) triple into the
  // table's results/ridesById/editValues/termReasonsById state.
  const applyResults = useCallback((resultList, rideList, sheetList = []) => {
    const map = Object.fromEntries(rideList.map((r) => [r.id, r]));
    setResults(sortResults(resultList, map));
    setRidesById(map);
    // Card #2063 AC3 — sheets grouped by rideId, then resolveDisplayTermReason
    // per ride (score-common's 4-tier deriveRideTermReason read path, PLUS
    // #2059/#2840's bare-SCRATCHED-with-no-recorded-reason fallback -- a
    // real, current production state, see project_term_reason_backfill_unrun).
    // Card #2840 -- this used to call deriveRideTermReason directly, which
    // misses that bare-SCRATCHED case entirely (its contract is deliberately
    // unchanged), so a scratched ride with no stamped reason got no chip, no
    // term-block on a stale placing, and (before this card) no score
    // suppression either. resolveDisplayTermReason is the same resolver
    // score-api/report-defs/report-api already standardized on for exactly
    // this reason. A ride with no term reason at all (never terminated) is
    // simply absent from the map rather than carrying an explicit null, so
    // callers can test presence with `termReasonsById[rideId]`.
    //
    // #2246 — hoisted ABOVE the setEditValues seed below, which now needs it.
    // Order is load-bearing, not cosmetic: see the pre-fill's own term-reason
    // gate for why.
    // #2974 — this batch read had no authority reference at all. Filtered
    // here (not left to resolveDisplayTermReason's own internal
    // filterAuthoritativeScoreSheets, score-common/utils/ride-status.js) so
    // every consumer of sheetsByRideId, present or future, sees only the
    // authoritative sheet per seat rather than depending on one particular
    // downstream function's own defensive filter.
    const sheetsByRideId = new Map();
    for (const sheet of authoritativeSheetsOnly(sheetList)) {
      if (!sheet?.rideId) continue;
      const list = sheetsByRideId.get(sheet.rideId);
      if (list) list.push(sheet);
      else sheetsByRideId.set(sheet.rideId, [sheet]);
    }
    const nextTermReasons = {};
    for (const ride of rideList) {
      const reason = resolveDisplayTermReason(ride, sheetsByRideId.get(ride.id) ?? []);
      if (reason) nextTermReasons[ride.id] = reason;
    }
    setTermReasonsById(nextTermReasons);
    // #2246 — pre-fill the Placing box from the place the JUDGE already wrote
    // in the sheet's own Place column (`judgeEnteredPlace`, #1752, carried on
    // every getClassResults row). A breed show is mostly place-only classes
    // (DSHB CHAMPIONSHIP/SUITABILITY, USEF DR204.2.c/DR210.5), which award NO
    // percentage at all, so the ranking-based suggestion this screen has always
    // shown has nothing to rank and the operator retypes, by hand, a number the
    // judge already recorded — duplicate entry on the exact path that releases
    // to FVD. DR211.5 (docs/rules/usef/rulebook-dr-dressage-2026.pdf, © USEF
    // 2026): "all scores (if given) and placings must be published".
    //
    // This changes NOTHING about what gets committed (Jaybill's 2026-09-03
    // ruling on the card: do the pre-fill, do NOT remove the admin placing
    // step). editValues is input state only; PUT /class/:classId/placings, run
    // by the PLACE button, is still the sole writer of Ride.placing, and the
    // operator can edit or clear a pre-filled box first. The admins already
    // know to press PLACE, so their workflow is unchanged.
    //
    // Three gates, in order:
    //  1. A COMMITTED Ride.placing always wins — the judge's sheet place never
    //     overwrites a placing that was already placed (0 included: #1785/#1839,
    //     a stored 0 is a real placing, not an absence, hence `!= null`).
    //  2. Only a POSITIVE integer seeds — matches score-common's own
    //     judgeEnteredPlaceOf and score-api's judgeEnteredPlaceFromSheets, so a
    //     stored 0 or a junk value is never promoted to a 1st place.
    //  3. A TERMINATED ride never seeds, even carrying a judge place. This is
    //     the #2057/#2069 trap the termBlockedEntries comment below describes,
    //     approached from the other side: a terminated ride cannot hold a real
    //     placing, so pre-filling one would hand the operator a row that is
    //     pre-blocked from placing through no action of their own, and 409 the
    //     whole batch on score-api's ride_terminated guard if it slipped past.
    //     nextTermReasons is why the block above had to be hoisted.
    setEditValues(
      Object.fromEntries(
        resultList.map((r) => {
          if (r.placing != null) return [r.rideId, String(r.placing)];
          const judgePlace = r.judgeEnteredPlace;
          const seedable =
            typeof judgePlace === "number" &&
            Number.isInteger(judgePlace) &&
            judgePlace > 0 &&
            !nextTermReasons[r.rideId];
          return [r.rideId, seedable ? String(judgePlace) : ""];
        })
      )
    );
  }, []);

  useEffect(() => {
    setResults([]);
    setRidesById({});
    setPlaceBanner(null);
    // #2862 — a class switch always re-locks: the override is per-class, never
    // carried over from whatever the operator unlocked last.
    setOverrideUnlocked(false);
    if (!classId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setLoadingResults(true);
        setError(null);
        const { resultList, rideList, sheetList } = await loadResults(classId);
        if (cancelled) return;
        applyResults(resultList, rideList, sheetList);
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoadingResults(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [classId, loadResults, applyResults]);

  // #945 — which placing view is active: "score" (composite %, ignoring
  // division) or "division" (grouped by rider division, then score). Purely
  // a display/grouping concern — it never reorders `results` itself (the
  // state loadResults/applyResults populate and handlePlace submits from),
  // so switching views mid-entry can't disturb the edit buffers below.
  const [viewMode, setViewMode] = useState(PLACING_VIEWS.DEFAULT);

  // #2000 — apply resolvePlacingView's show-level default exactly ONCE per
  // activeShowId, as soon as activeShow is loaded (loadingShow false and
  // activeShow populated arrive together, see ActiveShowContext's activeShow
  // useMemo). Keying the "already applied" guard on activeShowId (not on
  // activeShow's object identity, which can change on background refetch,
  // or on viewMode) means: switching TO a different show re-resolves its
  // OWN default (AC2 — one show's setting never leaks into another's
  // display), while any manual Select change the operator makes afterward
  // for the SAME show is never overwritten out from under them (AC3 — a
  // class-level/session choice, once made, wins).
  const appliedDefaultViewShowIdRef = useRef(null);
  useEffect(() => {
    if (!activeShowId || loadingShow) return;
    if (appliedDefaultViewShowIdRef.current === activeShowId) return;
    appliedDefaultViewShowIdRef.current = activeShowId;
    setViewMode(resolvePlacingView(activeShow));
  }, [activeShowId, activeShow, loadingShow]);

  // Live-parsed view of every row's edit buffer — recomputed as the operator
  // types, so both the Place button and each row's TextField reflect validity
  // without a network round-trip (#839 — batch entry, no per-row save).
  const parsedEntries = useMemo(
    () => results.map((r) => ({ rideId: r.rideId, ...parsePlacingInput(editValues[r.rideId]) })),
    [results, editValues]
  );
  const invalidRideIds = useMemo(
    () => new Set(parsedEntries.filter((e) => !e.ok).map((e) => e.rideId)),
    [parsedEntries]
  );

  // #953 — USEF placing suggestion per placeable (non-term'd) ride, keyed by
  // rideId (see suggestedPlacingsFor's doc comment). Computed independent of
  // viewMode — cheap, pure math over already-loaded `results` — so the
  // "Suggested" column can look a row's suggestion up directly at render
  // time without re-deriving buildPlacingGroups' internals. Purely a display
  // aid: never read by handlePlace, never written back to `results` or
  // editValues, so it can't affect what actually gets placed.
  const suggestedPlacings = useMemo(
    () =>
      suggestedPlacingsFor(
        results.filter((r) => !isBottomTierRow(r, ridesById)),
        ridesById,
        catalogDefsById,
        selectedClass
      ),
    [results, ridesById, catalogDefsById, selectedClass]
  );

  // #1999 (AC5): display-only tie-break signal column. Which of
  // row.collectiveTotal / row.artisticTotal (if either) is the number
  // DR123.3/DR129.5 actually use to break a percentage tie for this class's
  // classType, per score-common's tieBreakDisplayFieldForClassType (see
  // that export's comment for why this is exactly two buckets, not three).
  // Resolved off the class's real classType the same way suggestedPlacings
  // already does (resolveClassTypeForSuggestion), so a Freestyle class shows
  // Artistic Marks and an Equitation/FEI Children class shows no column at
  // all (score sheets and rules for those carry neither number).
  // DISPLAY ONLY: this memo is never read by handlePlace, suggestedPlacings,
  // compareByComposite, or tieIndicatorsFor, so it cannot affect ordering.
  const tieBreakField = useMemo(
    () =>
      tieBreakDisplayFieldForClassType(
        resolveClassTypeForSuggestion(results, ridesById, catalogDefsById, selectedClass)
      ),
    [results, ridesById, catalogDefsById, selectedClass]
  );

  // #945 — the grouped/sorted display rows for the active view (see
  // buildPlacingGroups' doc comment). Recomputed from `results`, never
  // written back to it.
  const displayGroups = useMemo(
    () => buildPlacingGroups(results, ridesById, viewMode, t, catalogDefsById, selectedClass),
    [results, ridesById, viewMode, t, catalogDefsById, selectedClass]
  );

  // #904 — duplicate-placing guard: nothing previously stopped the same
  // placing value from being entered on two different rides (#898 saw two
  // rides both land on placing 1 with no warning before Place committed and
  // released to FVD). Bottom-tier rows (scratched/eliminated, #811 — never
  // competitively placed) are excluded so their blank/irrelevant edit values
  // can't false-positive a collision. Blank stays "unplaced" and never
  // participates. Grouped by value so the banner can name exactly which
  // rides collide on which placing.
  // #945/#2049 — in the "division" view the same placing legitimately
  // repeats once per division (e.g. every division's own 1st place), so #945
  // skipped the check entirely there. #2001 then made the division view the
  // system default, leaving the guard OFF on the default path of every
  // unconfigured show. Decided on #2049: scope the check WITHIN each division
  // group instead of skipping it — same division + same placing is an entry
  // error and warns/blocks; a cross-division repeat stays silent (#945's
  // original intent). The scope key is divisionKeyForRow's normalized key,
  // the same one the displayed grouping uses, so the guard always matches
  // the groups on screen. Non-division views keep the flat class-wide check.
  const duplicatePlacingGroups = useMemo(() => {
    const byScope = new Map();
    for (const row of results) {
      if (isBottomTierRow(row, ridesById)) continue;
      const entry = parsedEntries.find((e) => e.rideId === row.rideId);
      if (!entry?.ok || entry.value == null) continue;
      const scope =
        viewMode === PLACING_VIEWS.DIVISION
          ? `${divisionKeyForRow(row, ridesById)}::${entry.value}`
          : entry.value;
      const group = byScope.get(scope) ?? { value: entry.value, rideIds: [] };
      group.rideIds.push(row.rideId);
      byScope.set(scope, group);
    }
    return [...byScope.values()]
      .filter((g) => g.rideIds.length > 1)
      .sort((a, b) => a.value - b.value);
  }, [results, ridesById, parsedEntries, viewMode]);
  const duplicatePlacingRideIds = useMemo(
    () => new Set(duplicatePlacingGroups.flatMap((g) => g.rideIds)),
    [duplicatePlacingGroups]
  );

  // #2057 -- client-side pre-check for the #2069 server-side hard block: a
  // ride carrying a resolved term reason (termReasonsById, deriveRideTermReason's
  // 4-tier read path, populated in applyResults) cannot hold a REAL placing
  // (0 included, per #1785/#1839 -- 0 is a stored placing, not an absence).
  // The trap this closes: applyResults seeds editValues from each ride's
  // STORED placing, so a legacy ride that is terminated but still carries an
  // old placing value keeps resubmitting that number on every Place press,
  // 409ing the entire batch with score-api's ride_terminated guard (results.js
  // ~line 1386) and no per-row indication of which ride is at fault. This
  // catches it locally before the network round-trip, naming the specific
  // row(s) and their specific DR123.3 outcome (AC1/AC2) -- unlike
  // duplicatePlacingGroups this has NO skip/override: Jaybill's 2026-08-30
  // decision on #2057/#2069 rejected warn-and-allow outright, so there is no
  // "place anyway" path here, only "blank the row and place again".
  const termBlockedEntries = useMemo(() => {
    const list = [];
    for (const entry of parsedEntries) {
      if (!entry.ok || entry.value == null) continue;
      const termReason = termReasonsById[entry.rideId];
      if (termReason) list.push({ rideId: entry.rideId, termReason });
    }
    return list;
  }, [parsedEntries, termReasonsById]);
  const termBlockedRideIds = useMemo(
    () => new Set(termBlockedEntries.map((e) => e.rideId)),
    [termBlockedEntries]
  );

  // #2862 — is the SELECTED class already placed? The loose "any committed
  // placing" signal (deriveClassSummary.anyPlaced's exact rule, and what
  // score-api's classHasCommittedPlacings gates on): a partially-placed class
  // is a placed class for lock purposes. Computed off the live `results` (not
  // classSummaries) so it flips the instant a Place commit reloads the table.
  const classIsPlaced = useMemo(() => results.some((r) => r.placing != null), [results]);
  // Read-only for EVERYONE until an override holder explicitly unlocks; a
  // non-admin never sees the unlock action, so for them a placed class is
  // simply, permanently read-only (AC1).
  const placingsLocked = classIsPlaced && !overrideUnlocked;

  // The single Place action (#839): validates every row locally first (no
  // network call for a plain format error), then commits the WHOLE class in
  // one atomic request. score-api itself re-validates + re-checks
  // releasability server-side and blocks (409, nothing written) if the class
  // isn't fully releasable yet — that's the authoritative gate, this is just
  // a fast local pre-check. #904 — a duplicate-placing collision warns
  // instead of silently committing; the operator can press "Place anyway"
  // (skipDuplicateCheck) to proceed, since legitimate ties do happen.
  const handlePlace = async ({ skipDuplicateCheck = false } = {}) => {
    // #2862 — defense in depth behind the disabled Place button: a locked
    // class never commits locally either (the server would 403 it anyway).
    if (placingsLocked) return;
    if (invalidRideIds.size > 0) {
      setPlaceBanner({ severity: "error", message: t("placings.fixInvalidBeforePlacing") });
      return;
    }
    // #2057 -- checked before the duplicate-placing warning (and with no
    // skip flag, unlike that one): a resolved term reason is a hard block
    // per Jaybill's 2026-08-30 decision, never a warn-and-allow. Catching it
    // here means the class-wide error names every offending row up front
    // instead of surfacing only after the whole batch 409s server-side.
    if (termBlockedEntries.length > 0) {
      setPlaceBanner({
        severity: "error",
        message: t("placings.termBlockedWarning"),
        termBlockedEntries,
      });
      return;
    }
    if (!skipDuplicateCheck && duplicatePlacingGroups.length > 0) {
      setPlaceBanner({
        severity: "warning",
        message: t("placings.duplicatePlacingWarning"),
        duplicateGroups: duplicatePlacingGroups,
      });
      return;
    }
    setPlaceSubmitting(true);
    setPlaceBanner(null);
    try {
      const payload = {
        placings: parsedEntries.map(({ rideId, value }) => ({ rideId, placing: value })),
      };
      const data = unwrap(await scoreApi.results.setClassPlacings(classId, payload));
      setPlaceBanner({
        severity: "success",
        message: t("placings.placeSuccess", { count: data.placed }),
      });
      // #822 attaches here — see the function's own doc comment above.
      await triggerScoresheetGenAndEmail(classId, data);

      const { resultList, rideList, sheetList } = await loadResults(classId);
      applyResults(resultList, rideList, sheetList);
      setClassSummaries((prev) => ({ ...prev, [classId]: deriveClassSummary(resultList) }));
      // #2862 — the override is per-CHANGE, not per-session: a successful
      // commit re-locks the class, so the next edit needs another explicit
      // unlock (and can't ride along silently on a stale override).
      setOverrideUnlocked(false);
    } catch (err) {
      // The shared axios interceptor (common-fe) rewrites err.message to a
      // generic "Server Error" so raw backend text never leaks by accident —
      // the curated 409/400 messages below read err.response directly instead,
      // which is the documented escape hatch for exactly this case.
      const status = err?.response?.status;
      const failData = err?.response?.data?.data;
      // #2057 -- score-api's setClassPlacings (results.js ~line 1386) 409s
      // with a DIFFERENT payload shape (code ride_terminated, a
      // `terminatedRides` array) than the pre-existing releasability 409
      // (code not_releasable, `blockingRideIds`/`blockingRides`) checked
      // below. This is the client-side pre-check's defense-in-depth
      // fallback (a race between load and submit, or a reason only the
      // server's own fetchAllSheetsForRide call resolves) -- the pre-check
      // above is expected to catch the common case before the request ever
      // goes out.
      if (status === 409 && failData?.terminatedRides?.length > 0) {
        setPlaceBanner({
          severity: "error",
          message: failData?.message || t("placings.termBlockedWarning"),
          termBlockedEntries: failData.terminatedRides,
        });
      } else if (status === 409) {
        setPlaceBanner({
          severity: "warning",
          message: failData?.message || t("placings.placeBlockedHeading"),
          blockingRideIds: failData?.blockingRideIds || [],
          // #902 — richer {rideId, wasEverConfirmed} shape (falls back to
          // undefined on an older server response; the banner render then
          // derives an all-false list from blockingRideIds instead).
          blockingRides: failData?.blockingRides,
        });
      } else if (status === 400) {
        setPlaceBanner({ severity: "error", message: failData?.message || t("placings.placeError") });
      } else {
        setPlaceBanner({ severity: "error", message: t("placings.placeError") });
      }
    } finally {
      setPlaceSubmitting(false);
    }
  };

  // #902 — resolve a single blocking ride inline, right from the banner.
  // #1268 removed the "forceFinal" branch this used to offer for a
  // reopened-but-previously-confirmed ride (finalizeRide is gone entirely);
  // "scratch" mirrors RideEditor's AC6 scratch toggle — for a ride that
  // genuinely never happened. Reloads the table + optimistically drops the
  // resolved ride out of the banner's blocking list (rather than
  // re-submitting Place) so the operator can resolve several blockers in a
  // row before pressing Place again.
  const handleResolveBlockingRide = async (rideId, action) => {
    setResolvingRideId(rideId);
    setResolveError(null);
    try {
      if (action === "scratch") {
        await scoreApi.rides.setRideStatus(rideId, { status: RIDE_STATUSES.SCRATCHED });
      }
      const { resultList, rideList, sheetList } = await loadResults(classId);
      applyResults(resultList, rideList, sheetList);
      setClassSummaries((prev) => ({ ...prev, [classId]: deriveClassSummary(resultList) }));
      setPlaceBanner((prev) => {
        if (!prev?.blockingRides?.length) return prev;
        const blockingRides = prev.blockingRides.filter((r) => r.rideId !== rideId);
        if (blockingRides.length === 0) return null;
        return { ...prev, blockingRides, blockingRideIds: blockingRides.map((r) => r.rideId) };
      });
    } catch (err) {
      setResolveError(err);
    } finally {
      setResolvingRideId(null);
    }
  };

  if (loadingShow) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom color="text.primary">
          {t("placings.title")}
        </Typography>
        <TableSkeleton rows={4} columns={2} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom color="text.primary">
        {t("placings.title")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message || t("placings.loadError")}
        </Alert>
      )}

      {!activeShowId && (
        <Typography color="text.secondary">{t("ring.noShows")}</Typography>
      )}

      {activeShowId && loadingClasses && <TableSkeleton rows={5} columns={2} />}

      {activeShowId && !loadingClasses && classes.length === 0 && (
        <Typography color="text.secondary">{t("placings.noClasses")}</Typography>
      )}

      {activeShowId && !loadingClasses && classes.length > 0 && (
        <Stack direction={{ xs: "column", md: "row" }} spacing={3} sx={{ mb: 3 }}>
          {/* #803 — the class list itself is the picker: it renders on load,
              filterable by number/name/ring, sorted by scheduled time earliest
              → latest. A class still missing placings carries a "Needs
              placings" badge instead of being floated to the top (#599's old
              behavior), so the operator works the show in ring order and can
              still spot outstanding classes at a glance. Selecting a class
              loads its placings table on the right. */}
          <Box sx={{ width: { xs: "100%", md: 360 }, flexShrink: 0 }}>
            <TextField
              size="small"
              fullWidth
              placeholder={t("ring.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="placings-search"
              sx={{ mb: 1 }}
            />
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", mb: 1 }}>
              {/* #2855 — the client's two DEFAULT-ON filters (see the state
                  declarations above): today's classes only, and hide classes
                  that already have placings set. Both opt out per-link via
                  ?today=0 / ?hidePlaced=0. */}
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={todayOnly}
                    onChange={(e) => setTodayOnly(e.target.checked)}
                    data-testid="placings-today-only-toggle"
                  />
                }
                label={t("placings.todayOnly")}
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={hidePlaced}
                    onChange={(e) => setHidePlaced(e.target.checked)}
                    data-testid="placings-hide-placed-toggle"
                  />
                }
                label={t("placings.hidePlaced")}
              />
              {/* Product follow-up to #803 — toggle to just the classes still
                  missing placings; composes with search above. MUI Switch
                  defaults to `primary`, which is grey/near-invisible in this
                  brand's dark theme, so the color is pinned explicitly to
                  `warning` (matching the badge it filters for) instead. */}
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    color="warning"
                    checked={needsPlacingsOnly}
                    onChange={(e) => setNeedsPlacingsOnly(e.target.checked)}
                    data-testid="placings-needs-only-toggle"
                  />
                }
                label={t("placings.needsPlacingsOnly")}
              />
            </Box>
            <List
              dense
              data-testid="placings-class-list"
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                maxHeight: 520,
                overflow: "auto",
              }}
            >
              {filteredClasses.length === 0 && (
                <Typography color="text.secondary" sx={{ p: 2 }} data-testid="placings-no-filtered-results">
                  {t("placings.noSearchResults")}
                </Typography>
              )}
              {filteredClasses.map((c) => {
                const summary = classSummaries[c.id];
                const time = formatClassStartTime(c.scheduledTime) || t("placings.noTime");
                const secondary = c.ring
                  ? `${t("ring.ringLabel", { ring: c.ring })} · ${time}`
                  : time;
                return (
                  <ListItemButton
                    key={c.id}
                    selected={c.id === classId}
                    onClick={() => selectClassId(c.id)}
                    data-testid={`placings-class-${c.id}`}
                  >
                    {/* #981 — the picker's list entries are plain (unlinked)
                        text; the class link lives on the placing screen's
                        selected-class heading instead (top-right, below),
                        so the picker's click always just selects the class
                        for placing without a competing nested-anchor
                        navigation target. */}
                    <ListItemText primary={classNumberLabel(c)} secondary={secondary} />
                    {summary?.finalUnplaced && (
                      <Chip
                        size="small"
                        color="warning"
                        label={t("placings.needsPlacings")}
                        data-testid={`placings-needs-placings-${c.id}`}
                        sx={{ ml: 1 }}
                      />
                    )}
                  </ListItemButton>
                );
              })}
            </List>
          </Box>

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            {/* #981 — the selected class's own heading, top-right of the
                placing screen, is the (only) link into its class detail —
                the picker to the left no longer doubles as a link target. */}
            {classId && selectedClass && (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", mb: 2 }}>
                <Typography variant="h6" component="div" data-testid="placings-selected-class-heading">
                  <ClassLink classObj={selectedClass} />
                </Typography>
                {/* #2855 — the client's header asks: the selected class's ring
                    name/number and scheduled start time, right under its
                    heading. Same renderers the picker's secondary line uses
                    (ring.ringLabel + formatClassStartTime, the FVD-stamp-zone
                    inverse — see classDisplay.js), so the two can never
                    disagree on what the ring or the time reads. */}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  data-testid="placings-selected-class-meta"
                >
                  {selectedClass.ring
                    ? t("ring.ringLabel", { ring: selectedClass.ring })
                    : t("ring.unassignedRing")}
                  {" · "}
                  {formatClassStartTime(selectedClass.scheduledTime) || t("placings.noTime")}
                </Typography>
              </Box>
            )}

            {!classId && (
              <Typography color="text.secondary">
                {t("placings.selectClassPrompt")}
              </Typography>
            )}

            {classId && !loadingResults && results.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t("placings.batchHelp")}
              </Typography>
            )}

            {/* #945 — placing sort view toggle: "score" ranks every ride by
                composite % regardless of division; "division" groups by the
                rider's division (Ride.riderStatus, #821), each group ranked
                by composite %, with the duplicate-placing warning below
                suppressed in this view (same placing repeats legitimately
                once per division). */}
            {classId && !loadingResults && results.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 220, mb: 2 }}>
                <InputLabel id="placings-view-label">{t("placings.view.label")}</InputLabel>
                <Select
                  labelId="placings-view-label"
                  label={t("placings.view.label")}
                  value={viewMode}
                  onChange={(e) => {
                    setViewMode(e.target.value);
                    // #1595 — a MANUAL pick is the operator's preference;
                    // persist it so the next visit resolves to it (step 3
                    // of resolvePlacingView). The show-default effect above
                    // deliberately does not persist — applying a show's
                    // configured default is not an operator choice.
                    storePlacingView(e.target.value);
                  }}
                  data-testid="placings-view-select"
                >
                  <MenuItem value={PLACING_VIEWS.DEFAULT} data-testid="placings-view-default">
                    {t("placings.view.default")}
                  </MenuItem>
                  <MenuItem value={PLACING_VIEWS.SCORE} data-testid="placings-view-score">
                    {t("placings.view.score")}
                  </MenuItem>
                  <MenuItem value={PLACING_VIEWS.DIVISION} data-testid="placings-view-division">
                    {t("placings.view.division")}
                  </MenuItem>
                  <MenuItem value={PLACING_VIEWS.SUGGESTED} data-testid="placings-view-suggested">
                    {t("placings.view.suggested")}
                  </MenuItem>
                </Select>
              </FormControl>
            )}

            {/* #2862 — the placed-class lock banner. Read-only for everyone
                once placings are set; the explicit "Unlock to edit" override
                action renders ONLY for a score:override_placed_class holder
                (score_admin) — a non-admin gets the lock with no affordance
                (AC1), an admin gets the explicit override (AC2). */}
            {classId && !loadingResults && classIsPlaced && (
              <Alert
                severity={placingsLocked ? "info" : "warning"}
                sx={{ mb: 2 }}
                data-testid="placings-lock-banner"
                action={
                  canOverridePlacedClass && placingsLocked ? (
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => setOverrideUnlocked(true)}
                      data-testid="placings-override-unlock"
                    >
                      {t("placings.unlockToEdit")}
                    </Button>
                  ) : undefined
                }
              >
                {placingsLocked
                  ? canOverridePlacedClass
                    ? t("placings.lockedAdmin")
                    : t("placings.lockedReadOnly")
                  : t("placings.overrideActive")}
              </Alert>
            )}

            {placeBanner && (
              <Alert severity={placeBanner.severity} sx={{ mb: 2 }} data-testid="placings-banner">
                {placeBanner.message}
                {placeBanner.blockingRideIds?.length > 0 && (
                  <Box component="ul" sx={{ m: "8px 0 0", pl: 2 }}>
                    {/* #902 — blockingRides (rich {rideId, wasEverConfirmed} shape) drives
                        the reopened-vs-never-scored distinction; blockingRideIds alone
                        (older/back-compat 409 shape) falls back to "never scored" styling. */}
                    {(
                      placeBanner.blockingRides ||
                      placeBanner.blockingRideIds.map((rideId) => ({ rideId, wasEverConfirmed: false }))
                    ).map(({ rideId, wasEverConfirmed }) => (
                      <li key={rideId} data-testid={`placings-blocking-ride-${rideId}`}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ flexWrap: "wrap", rowGap: 0.5 }}
                        >
                          <RideLink ride={ridesById[rideId]} rideId={rideId} fallback={rideId} />
                          <Chip
                            size="small"
                            color={wasEverConfirmed ? "warning" : "default"}
                            variant={wasEverConfirmed ? "filled" : "outlined"}
                            label={
                              wasEverConfirmed
                                ? t("placings.blockingReopened")
                                : t("placings.blockingNeverScored")
                            }
                            data-testid={`placings-blocking-status-${rideId}`}
                          />
                          {/* #2169 -- the only inline resolution the banner previously
                              offered a reopened-confirmed ride was Scratch, the wrong
                              Term Reason (the horse rode; it was not scratched). Rather
                              than surface the real reopen/re-confirm choice on THIS
                              screen (rejected -- that is a scoring decision and belongs
                              in the ride editor, per Jaybill's 2026-09-01 decision on
                              the card), this button only navigates straight to the
                              blocking ride in RideEditor (App.jsx `/rides/:rideId`,
                              same route RideLink above already uses) so the admin
                              reaches the correct path in one click instead of a hunt.
                              Plain react-router Link rendered as a Button: no onClick,
                              no scoreApi call, so it cannot reopen, confirm, terminate,
                              or otherwise mutate the ride -- it can only navigate. */}
                          <Button
                            size="small"
                            variant="outlined"
                            // #902's dark-mode-invisible-outlined-button trap
                            // applies to any unset outlined color, not just
                            // the one it was first found on -- pin "info" so
                            // this reads in both themes.
                            color="info"
                            component={RouterLink}
                            to={`/rides/${rideId}`}
                            data-testid={`placings-open-ride-${rideId}`}
                          >
                            {t("placings.openRide")}
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            disabled={resolvingRideId === rideId}
                            onClick={() => handleResolveBlockingRide(rideId, "scratch")}
                            data-testid={`placings-scratch-${rideId}`}
                          >
                            {resolvingRideId === rideId ? t("placings.resolving") : t("placings.scratch")}
                          </Button>
                        </Stack>
                      </li>
                    ))}
                  </Box>
                )}
                {/* #2057 -- names every blocked row AND its specific
                    DR123.3 outcome (AC2), matching this same banner's
                    blockingRideIds treatment above. No "place anyway" here:
                    unlike the duplicate-placing warning below, a term reason
                    is a hard block with no override (Jaybill's 2026-08-30
                    decision) -- the only recovery is blanking the row's
                    placing, then pressing Place again. */}
                {placeBanner.termBlockedEntries?.length > 0 && (
                  <Box component="ul" sx={{ m: "8px 0 0", pl: 2 }} data-testid="placings-term-blocked-warning">
                    {placeBanner.termBlockedEntries.map(({ rideId, termReason }) => (
                      <li key={rideId} data-testid={`placings-term-blocked-${rideId}`}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ flexWrap: "wrap", rowGap: 0.5 }}
                        >
                          <RideLink ride={ridesById[rideId]} rideId={rideId} fallback={rideId} />
                          <Chip
                            size="small"
                            color="error"
                            variant="outlined"
                            label={termReason}
                            data-testid={`placings-term-blocked-reason-${rideId}`}
                          />
                        </Stack>
                      </li>
                    ))}
                  </Box>
                )}
                {placeBanner.duplicateGroups?.length > 0 && (
                  <Box sx={{ mt: 1 }} data-testid="placings-duplicate-warning">
                    <Box component="ul" sx={{ m: "8px 0", pl: 2 }}>
                      {placeBanner.duplicateGroups.map(({ value, rideIds }) => (
                        <li key={value} data-testid={`placings-duplicate-group-${value}`}>
                          {t("placings.duplicatePlacingGroup", { value })}
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ flexWrap: "wrap", rowGap: 0.5, mt: 0.5 }}
                          >
                            {rideIds.map((rideId) => (
                              <RideLink
                                key={rideId}
                                ride={ridesById[rideId]}
                                rideId={rideId}
                                fallback={rideId}
                              />
                            ))}
                          </Stack>
                        </li>
                      ))}
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      // #904 — same dark-mode-legibility fix #902 applied to
                      // the banner's resolve button: MUI's unset outlined color
                      // is near-invisible in this brand's dark theme.
                      color="warning"
                      disabled={placeSubmitting}
                      onClick={() => handlePlace({ skipDuplicateCheck: true })}
                      data-testid="placings-place-anyway"
                    >
                      {t("placings.placeAnyway")}
                    </Button>
                  </Box>
                )}
              </Alert>
            )}
            {resolveError && (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                onClose={() => setResolveError(null)}
                data-testid="placings-resolve-error"
              >
                {resolveError?.response?.data?.data?.message || t("placings.resolveError")}
              </Alert>
            )}

            {classId && loadingResults && <TableSkeleton rows={4} columns={6} />}
            {classId && !loadingResults && results.length === 0 && (
              <Typography color="text.secondary">{t("placings.noResults")}</Typography>
            )}
            {classId && !loadingResults && results.length > 0 && (
              <>
                <TableContainer data-testid="placings-table">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t("placings.columns.placing")}</TableCell>
                        {/* #953 — advisory-only USEF suggested order, shown
                            next to the operator's own placing input so it's
                            easy to compare; never pre-filled into that
                            input (see the Placing cell below, unchanged). */}
                        {viewMode === PLACING_VIEWS.SUGGESTED && (
                          <TableCell>{t("placings.columns.suggested")}</TableCell>
                        )}
                        <TableCell>{t("placings.columns.ride")}</TableCell>
                        <TableCell>{t("placings.columns.test")}</TableCell>
                        <TableCell>{t("placings.columns.composite")}</TableCell>
                        {/* #1999 (AC5): DR123.3/DR129.5 tie-break signal
                            column. Display only, header text and presence
                            both come from tieBreakField (computed for the
                            selected class's real classType above); a
                            classType with no display field (Equitation, FEI
                            Children) renders no column at all rather than a
                            column full of dashes. */}
                        {tieBreakField && (
                          <TableCell data-testid="placings-tiebreak-header">
                            {t(TIE_BREAK_COLUMN_LABEL_KEYS[tieBreakField])}
                          </TableCell>
                        )}
                        <TableCell>{t("placings.columns.status")}</TableCell>
                        <TableCell>{t("placings.columns.division")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {/* #945 — displayGroups is `results` grouped/sorted per
                          the active view (buildPlacingGroups); a group with a
                          non-null label renders its own separator row ahead
                          of its rides (score view's single "all" group has
                          label:null so only the trailing term'd separator, if
                          any, shows — division view gets one per division). */}
                      {displayGroups.flatMap((group) => {
                        // #946 — ties are only meaningful within the same
                        // rendered group (e.g. not across two divisions in
                        // the division view, which place separately).
                        const tieIndicators = tieIndicatorsFor(group.rows);
                        const rows = group.rows.map((row) => (
                          <TableRow key={row.rideId} data-testid={`placing-row-${row.rideId}`}>
                            <TableCell sx={{ width: 140 }}>
                              <TextField
                                size="small"
                                type="number"
                                inputProps={{
                                  min: 0,
                                  step: 1,
                                  "aria-label": t("placings.columns.placing"),
                                  "data-testid": `placing-input-${row.rideId}`,
                                }}
                                // #904 — a duplicate placing is a valid number
                                // (so it doesn't trip the format `error` prop
                                // above), but still gets its own warning-colored
                                // outline so the collision is visible right on
                                // the row, not just in the banner.
                                sx={{
                                  width: 88,
                                  // #2855 — the client's screenshot ask: the
                                  // native type="number" spinner is oversized
                                  // in this compact cell. Removed (not just
                                  // shrunk) across the engines that render
                                  // one: Blink/WebKit via the two
                                  // ::-webkit-*-spin-button pseudo-elements,
                                  // Firefox via -moz-appearance on the input.
                                  // Keyboard/click entry is untouched; #2850's
                                  // blur-on-wheel guard below still stands on
                                  // its own (it fires on any wheel over the
                                  // box, spinner or not).
                                  "& input::-webkit-outer-spin-button": {
                                    WebkitAppearance: "none",
                                    margin: 0,
                                  },
                                  "& input::-webkit-inner-spin-button": {
                                    WebkitAppearance: "none",
                                    margin: 0,
                                  },
                                  "& input": { MozAppearance: "textfield" },
                                  ...(duplicatePlacingRideIds.has(row.rideId) &&
                                  !invalidRideIds.has(row.rideId) &&
                                  !termBlockedRideIds.has(row.rideId)
                                    ? {
                                        "& .MuiOutlinedInput-notchedOutline": {
                                          borderColor: "warning.main",
                                          borderWidth: 2,
                                        },
                                      }
                                    : {}),
                                }}
                                value={editValues[row.rideId] ?? ""}
                                disabled={placeSubmitting || placingsLocked}
                                // #2850 — Chromium steps a FOCUSED
                                // type="number" input's value on any trusted
                                // wheel event over it, so an operator
                                // scrolling the page with the cursor parked
                                // on a placing box silently rewrote the
                                // entered placing. Blur on wheel hands the
                                // gesture back to the page (which then
                                // scrolls normally) before the browser's
                                // step-the-focused-input default can apply;
                                // keyboard/click entry is untouched. Applies
                                // to every row, since this is the one
                                // per-row TextField the table renders.
                                onWheel={(e) => e.target.blur()}
                                // #2057 -- a term-blocked row's placing value is
                                // a real, valid integer (it wouldn't trip the
                                // format check invalidRideIds guards), but it
                                // still can't be placed, so it gets the same
                                // red `error` styling as a format error --
                                // visible right on the row, not only after a
                                // Place attempt surfaces the banner above.
                                error={invalidRideIds.has(row.rideId) || termBlockedRideIds.has(row.rideId)}
                                onChange={(e) =>
                                  setEditValues((prev) => ({
                                    ...prev,
                                    [row.rideId]: e.target.value,
                                  }))
                                }
                              />
                            </TableCell>
                            {/* #953 — advisory suggested placing + tie/rule
                                tooltip. Display-only: this cell never reads
                                from or writes to editValues, so it can't
                                affect what handlePlace actually commits. */}
                            {viewMode === PLACING_VIEWS.SUGGESTED && (
                              <TableCell data-testid={`placing-suggested-${row.rideId}`}>
                                {(() => {
                                  const suggestion = suggestedPlacings.get(row.rideId);
                                  if (!suggestion) {
                                    return (
                                      <Typography variant="body2" color="text.secondary">
                                        {t("placings.suggestion.unavailable")}
                                      </Typography>
                                    );
                                  }
                                  const isTied = suggestion.tieGroup.length > 1;
                                  const tooltip = suggestionTooltip(suggestion, t);
                                  const content = (
                                    <Stack direction="row" spacing={0.25} alignItems="center">
                                      <Typography variant="body2" component="span">
                                        {suggestion.placing}
                                      </Typography>
                                      <TieIndicator
                                        value={isTied ? "=" : undefined}
                                        rideId={`suggested-${row.rideId}`}
                                        t={t}
                                        labelOverride={tooltip}
                                        suppressTitle={!!tooltip}
                                      />
                                    </Stack>
                                  );
                                  return tooltip ? (
                                    <Tooltip title={tooltip} arrow>
                                      <Box component="span" sx={{ cursor: "default" }}>
                                        {content}
                                      </Box>
                                    </Tooltip>
                                  ) : (
                                    content
                                  );
                                })()}
                              </TableCell>
                            )}
                            <TableCell>
                              {/* #860 — shared RideLink ("#CNO, Rider on Horse"),
                                  linked into the RideEditor route. Sequenced
                                  ahead of #821 per the card note — #821 lands
                                  its rider status/div alongside this cell.
                                  #2858 — out of the TAB sequence (tabIndex -1,
                                  the same treatment scribe-fe's #535 gave its
                                  mouse-driven gavel/flag controls and #2714
                                  gave the ride editor's grid chrome): this
                                  link sits between two rows' placing inputs in
                                  DOM order, so leaving it tabbable made Tab
                                  hop ACROSS the row to the link instead of
                                  DOWN to the next placing field — the client's
                                  "tab order is going horizontal" complaint.
                                  Still rendered and mouse-clickable; the
                                  keyboard user reaches the same ride editor
                                  via the banner's Open-ride button or the
                                  class detail route. */}
                              <RideLink
                                ride={ridesById[row.rideId]}
                                rideId={row.rideId}
                                fallback={t("placings.unknownRide")}
                                tabIndex={-1}
                              />
                            </TableCell>
                            <TableCell data-testid={`placing-test-${row.rideId}`}>
                              {/* #915 — resolved per-ride off ride.testId against the
                                  bundled catalog (never per-class): a Test-of-Choice
                                  class can carry a different test per ride. */}
                              {getRideTestName(ridesById[row.rideId], catalogDefsById) || "—"}
                            </TableCell>
                            <TableCell>
                              {/* Card #2840 -- a term'd ride never reports a
                                  score value, so this never falls back to
                                  score-api's own real (non-null) composite
                                  for a sheet-level elimination (calculation.js
                                  zeroes it, it isn't withheld -- see
                                  score-suppression.js's header comment): the
                                  shared suppressScoreValue gate replaces
                                  formatPercentage's already-formatted string
                                  with the same em dash glyph every other
                                  surface uses, keyed off the SAME
                                  termReasonsById resolveDisplayTermReason
                                  already populated above. */}
                              {suppressScoreValue(termReasonsById[row.rideId], formatPercentage(row.composite))}
                              <TieIndicator
                                value={tieIndicators.get(row.rideId)}
                                rideId={row.rideId}
                                t={t}
                              />
                            </TableCell>
                            {/* #1999 (AC5): the raw points sum DR123.3/
                                DR129.5 actually use to separate this
                                percentage tie, not a percentage itself.
                                Display only: never read by handlePlace,
                                compareByComposite, or tieIndicatorsFor.
                                Card #2840 -- suppressed the same way the
                                Composite cell above is: a tie-break value is
                                still a subtotal a term'd ride must never
                                report. */}
                            {tieBreakField && (
                              <TableCell data-testid={`placing-tiebreak-${row.rideId}`}>
                                {suppressScoreValue(termReasonsById[row.rideId], formatTieBreakValue(row[tieBreakField]))}
                              </TableCell>
                            )}
                            <TableCell>
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <RideStatusBadge status={rideStatuses[row.rideId]} />
                                {/* Card #2063 AC3/AC5 — the SPECIFIC DR123.3
                                    term reason (Scratched/Excused/Eliminated/
                                    No Show), a SECOND chip rather than a
                                    RideStatusBadge change: RideStatusBadge
                                    (common-fe) is shared with scribe-fe's
                                    live kiosk screens (AC5), and adding a
                                    fifth non-status member there would mean
                                    verifying no kiosk regression for a
                                    scoreadmin-only need. termReasonsById only
                                    carries a key for a ride that actually
                                    has one (deriveRideTermReason's 4-tier
                                    read path in applyResults above), so this
                                    renders nothing for an ordinary scored
                                    ride. */}
                                {termReasonsById[row.rideId] && (
                                  <Chip
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    label={termReasonsById[row.rideId]}
                                    data-testid={`placing-term-reason-${row.rideId}`}
                                  />
                                )}
                                {/* #1886 AC2 — a committed placing is exactly
                                    what this page's Place button promises to
                                    "release to FVD" (see handlePlace above),
                                    so once `row.placing` is set, whether FVD
                                    actually confirmed it belongs right next
                                    to the status that reads Final, not only
                                    on the separate ClassManager list (#554).
                                    Absent fvdPushStatus reads NOT_POSTED
                                    (gray) here exactly like it does there —
                                    never silently omitted just because this
                                    is the Place screen. */}
                                {/* #1785 — a stored placing of 0 means
                                    UNPLACED, same as null; excluded here too
                                    so an actually-unplaced row never shows a
                                    "not released" badge for a release that
                                    was never attempted. */}
                                {/* #1886 — `row.writeBackBlocked` (results.js,
                                    stamped from the show's own `noFoxbox`
                                    admin flag since #1015) means this show
                                    was deliberately told never to push to
                                    FVD at all: foxbox's own IsReleased gate
                                    excludes these rides by design (see
                                    show-config.js's #1015 comment), so
                                    "release never happened" isn't a failure
                                    here, it's the configured behavior an
                                    admin already chose in Settings. Suppress
                                    the icon rather than invent a fourth
                                    state: a permanent gray/red badge on
                                    every placed row of a demo/test show
                                    would be constant noise about a release
                                    that was never going to occur, not signal
                                    — the same "doesn't apply, don't show it"
                                    call #1785 already made for placing 0. */}
                                {row.placing != null &&
                                  row.placing !== 0 &&
                                  !row.writeBackBlocked && (
                                  <FvdPushStatusIcon
                                    fvdPushState={row.fvdPushStatus ?? "NOT_POSTED"}
                                    error={row.fvdPushError}
                                    size={16}
                                  />
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell>
                              {/* #821 — rider's division. FVD has no literal division
                                  field; the genuine per-rider value is
                                  Ride.riderStatus (FVD tblRider.chrStatus, joined
                                  server-side in score-api's enrichRideNames — see
                                  #784). Blank for manual/unresolved rides, shown
                                  as a dash rather than invented. */}
                              <Typography variant="body2" color="text.secondary">
                                {ridesById[row.rideId]?.riderStatus || "—"}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ));
                        if (group.label == null) return rows;
                        return [
                          <TableRow key={`group-${group.key}`} data-testid={`placings-group-${group.key}`}>
                            <TableCell
                              colSpan={
                                (viewMode === PLACING_VIEWS.SUGGESTED ? 6 : 5) + (tieBreakField ? 1 : 0)
                              }
                              sx={{ bgcolor: "action.hover", fontWeight: 600 }}
                            >
                              {group.label}
                            </TableCell>
                          </TableRow>,
                          ...rows,
                        ];
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handlePlace}
                    disabled={placeSubmitting || placingsLocked}
                    data-testid="placings-place-button"
                  >
                    {placeSubmitting ? t("placings.placingInProgress") : t("placings.place")}
                  </Button>
                  {placeSubmitting && <CircularProgress size={20} />}
                </Stack>
              </>
            )}
          </Box>
        </Stack>
      )}
    </Box>
  );
}
